import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, CircularProgress, Card, CardContent,
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listMyAdvances, requestEmployeeAdvance, listCashAccounts, type PaymentListItem, type PaymentStatus,
} from '../../api/financiar'
import { getMyAdvanceBalance } from '../../api/deconturi'
import { errorMessage } from '../../api/client'

const STATUS_LABEL: Record<PaymentStatus, string> = { Draft: 'Ciornă', Executata: 'Executat', Anulata: 'Anulat' }
const STATUS_COLOR: Record<PaymentStatus, 'default' | 'success' | 'error'> = { Draft: 'default', Executata: 'success', Anulata: 'error' }

export default function AvansuriMelePage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data, isLoading } = useQuery({ queryKey: ['cheltuieli', 'avansuri-mele'], queryFn: listMyAdvances })
  const { data: balance } = useQuery({ queryKey: ['cheltuieli', 'sold-avans-mele'], queryFn: getMyAdvanceBalance })

  const columns = useMemo<GridColDef<PaymentListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 150, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'date', headerName: 'Dată', width: 110 },
    { field: 'amount', headerName: 'Sumă', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    { field: 'category', headerName: 'Motiv', flex: 1, minWidth: 160 },
    {
      field: 'status', headerName: 'Status', width: 120, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Cheltuieli</Typography>
        <Typography variant="inherit" color="text.primary">Avansurile mele</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Avansurile mele</Typography>
      <Card variant="outlined" sx={{ maxWidth: 320 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">Sold de avans deschis</Typography>
          <Typography variant="h4">{(balance?.balance ?? 0).toFixed(2).replace('.', ',')} RON</Typography>
        </CardContent>
      </Card>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>Cerere de avans nouă</Button>
      </Box>
      <Box sx={{ height: 500, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <NewAdvanceDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Stack>
  )
}

function NewAdvanceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: accounts } = useQuery({ queryKey: ['financiar', 'conturi'], queryFn: listCashAccounts, enabled: open })

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [cashAccountId, setCashAccountId] = useState('')
  const [currency, setCurrency] = useState('RON')
  const [exchangeRate, setExchangeRate] = useState('1')
  const [amount, setAmount] = useState('')
  const [category, setCategory] = useState('')
  const [notes, setNotes] = useState('')
  const [neededByDate, setNeededByDate] = useState('')

  const reset = () => {
    setDate(new Date().toISOString().slice(0, 10)); setCashAccountId(''); setCurrency('RON'); setExchangeRate('1');
    setAmount(''); setCategory(''); setNotes(''); setNeededByDate('')
  }

  const mutation = useMutation({
    mutationFn: () => requestEmployeeAdvance({
      date, cashAccountId, currency, exchangeRate: Number(exchangeRate), amount: Number(amount),
      category: category || null, notes: notes || null, neededByDate: neededByDate || null,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cheltuieli', 'avansuri-mele'] })
      reset()
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cerere de avans nouă</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={2}>
            <TextField type="date" label="Dată" value={date} onChange={(e) => setDate(e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
            <TextField type="date" label="Data la care e necesar" value={neededByDate} onChange={(e) => setNeededByDate(e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
          </Stack>
          <TextField select label="Cont" value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)} fullWidth>
            {accounts?.filter((a) => a.isActive).map((a) => <MenuItem key={a.id} value={a.id}>{a.name} ({a.currency})</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Monedă" value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ width: 110 }} />
            {currency !== 'RON' && <TextField type="number" label="Curs/RON" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} sx={{ width: 140 }} />}
            <TextField type="number" label="Sumă" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth />
          </Stack>
          <TextField label="Motiv / proiect asociat" value={category} onChange={(e) => setCategory(e.target.value)} fullWidth />
          <TextField label="Note" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={!cashAccountId || !amount || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => mutation.mutate()}>
          Trimite cererea
        </Button>
      </DialogActions>
    </Dialog>
  )
}
